import React, { useState, useEffect } from 'react';
import { Wrench, Plus, Sparkles, Loader2, AlertTriangle, CheckCircle, Clock, XCircle } from 'lucide-react';
import { maintenanceAPI, exportAPI } from '../../services/api';
import AssetSelect from '../../components/AssetSelect';
import ExportMenu from '../../components/ExportMenu';
import { useAuth } from '../../context/AuthContext';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import toast from 'react-hot-toast';
import clsx from 'clsx';

const TABS = [{ id:'alerts',label:'Degradation Alerts' },{ id:'workorders',label:'Work Orders' },{ id:'performance',label:'Performance Logs' }];

const ALERT_SEVERITY = {
  info:     'bg-blue-100 text-blue-700',
  warning:  'bg-amber-100 text-amber-700',
  critical: 'bg-red-100 text-red-700',
};

const ALERT_STATUS = {
  open:         { cls:'bg-red-50 text-red-700',     label:'Open'         },
  acknowledged: { cls:'bg-amber-50 text-amber-700', label:'Acknowledged' },
  resolved:     { cls:'bg-emerald-50 text-emerald-700',label:'Resolved'  },
  dismissed:    { cls:'bg-gray-50 text-gray-500',   label:'Dismissed'    },
};

const WO_STATUS = {
  open:        { cls:'bg-blue-100 text-blue-700',    label:'Open'        },
  in_progress: { cls:'bg-amber-100 text-amber-700',  label:'In Progress' },
  completed:   { cls:'bg-emerald-100 text-emerald-700',label:'Completed' },
  cancelled:   { cls:'bg-gray-100 text-gray-500',    label:'Cancelled'   },
};

const PRIORITY = {
  low:'bg-gray-100 text-gray-600', medium:'bg-blue-100 text-blue-700',
  high:'bg-amber-100 text-amber-700', critical:'bg-red-100 text-red-700'
};

const F = ({ label, children }) => <div><label className="block text-xs font-medium text-gray-600 mb-1">{label}</label>{children}</div>;
const I = (p) => <input {...p} className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500/30"/>;
const S = ({ children, ...p }) => <select {...p} className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500/30 bg-white">{children}</select>;

export default function MaintenancePage() {
  const { hasRole } = useAuth();
  const canWrite = hasRole('super_admin','operations_mgr');

  const [tab, setTab]           = useState('alerts');
  const [alerts, setAlerts]     = useState([]);
  const [workOrders, setWOs]    = useState([]);
  const [perfLogs, setPerfLogs] = useState([]);
  const [loading, setLoading]   = useState(true);
  const [selectedAlert, setSelectedAlert] = useState(null);
  const [rcaLoading, setRcaLoading]       = useState(false);
  const [woModal, setWoModal]   = useState(false);
  const [perfModal, setPerfModal] = useState(false);
  const [saving, setSaving]     = useState(false);
  const [assetFilter, setAssetFilter] = useState('');

  const today = new Date().toISOString().split('T')[0];
  const initWO = { asset_id:'', maintenance_type:'preventive', priority:'medium', title:'', description:'', scheduled_date: today, estimated_cost:'' };
  const initPerf = { asset_id:'', log_date: today, output_mwh:'', expected_mwh:'', availability_pct:'', ambient_temp_c:'', wind_speed_ms:'' };
  const [woForm, setWoForm]     = useState(initWO);
  const [perfForm, setPerfForm] = useState(initPerf);

  const loadAll = async () => {
    setLoading(true);
    try {
      const [a, wo, pl] = await Promise.all([
        maintenanceAPI.alerts({ status:'open', asset_id: assetFilter||undefined }),
        maintenanceAPI.workOrders({ asset_id: assetFilter||undefined }),
        maintenanceAPI.perfLogs({ asset_id: assetFilter||undefined }),
      ]);
      setAlerts(a.data.data);
      setWOs(wo.data.data.items || wo.data.data);
      setPerfLogs(pl.data.data);
    } catch {} finally { setLoading(false); }
  };

  useEffect(() => { loadAll(); }, [assetFilter]);

  const saveWO = async () => { setSaving(true); try { await maintenanceAPI.addWorkOrder(woForm); toast.success('Work order created'); setWoModal(false); loadAll(); } catch {} finally { setSaving(false); }};
  const savePerf = async () => { setSaving(true); try { await maintenanceAPI.addPerfLog(perfForm); toast.success('Performance log recorded'); setPerfModal(false); loadAll(); } catch {} finally { setSaving(false); }};

  const runRCA = async (alertId) => {
    setRcaLoading(true);
    try {
      const { data } = await maintenanceAPI.rootCause(alertId);
      setSelectedAlert(a => ({...a, ai_analysis: data.data.analysis}));
      toast.success('Root cause analysis complete');
    } catch {} finally { setRcaLoading(false); }
  };

  const updateWOStatus = async (id, status) => {
    try { await maintenanceAPI.updateWO(id, { status }); toast.success('Work order updated'); loadAll(); }
    catch {}
  };

  // Performance chart for selected asset
  const chartData = perfLogs.slice(0,30).reverse().map(l=>({
    date: l.log_date?.split('T')[0]?.slice(5),
    output: parseFloat(l.output_mwh||0),
    expected: parseFloat(l.expected_mwh||0),
    ratio: parseFloat(l.performance_ratio||0)*100,
  }));

  const criticalCount = alerts.filter(a=>a.severity==='critical').length;
  const openWOs = workOrders.filter(w=>w.status==='open'||w.status==='in_progress').length;

  const setW = (k) => (e) => setWoForm(f=>({...f,[k]:e.target.value}));
  const setP = (k) => (e) => setPerfForm(f=>({...f,[k]:e.target.value}));

  return (
    <div className="p-6 max-w-[1400px] mx-auto space-y-5">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-gray-900">Predictive Maintenance AI</h1>
          <p className="text-sm text-gray-500">Degradation alerts · Work orders · Performance tracking</p>
        </div>
        <ExportMenu exports={[{ label:'Work Orders Excel', apiFn: exportAPI.workOrdersExcel, filename:'work-orders.xlsx' }]}/>
          {canWrite && <div className="flex gap-2">
          <button onClick={()=>setPerfModal(true)} className="flex items-center gap-1.5 px-3 py-2 text-sm bg-white border border-gray-200 text-gray-700 rounded-lg hover:bg-gray-50"><Plus size={14}/>Log Performance</button>
          <button onClick={()=>setWoModal(true)} className="flex items-center gap-1.5 px-3 py-2 text-sm bg-primary-500 text-white rounded-lg hover:bg-primary-600"><Plus size={14}/>New Work Order</button>
        </div>}
      </div>

      {/* KPI strip */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {[['Critical Alerts', criticalCount, 'bg-red-50'],
          ['Open Work Orders', openWOs, 'bg-amber-50'],
          ['Total Alerts', alerts.length, 'bg-blue-50'],
          ['Perf Logs', perfLogs.length, 'bg-emerald-50']
        ].map(([l,v,bg])=>(
          <div key={l} className={clsx('rounded-xl border border-gray-200 p-4',bg)}>
            <p className="text-xs text-gray-500 uppercase tracking-wide font-medium">{l}</p>
            <p className="text-2xl font-bold text-gray-900 mt-1">{v}</p>
          </div>
        ))}
      </div>

      {/* Performance chart */}
      {chartData.length>0 && (
        <div className="bg-white rounded-xl border border-gray-200 p-5">
          <h3 className="text-sm font-semibold text-gray-700 mb-4">Performance Ratio Trend (%)</h3>
          <ResponsiveContainer width="100%" height={160}>
            <LineChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0"/>
              <XAxis dataKey="date" fontSize={11}/>
              <YAxis fontSize={11} domain={[0,110]}/>
              <Tooltip/>
              <Line type="monotone" dataKey="ratio" stroke="#1D6B4E" strokeWidth={2} dot={false} name="Performance %"/>
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-1 border-b border-gray-200">
        {TABS.map(t=>(
          <button key={t.id} onClick={()=>setTab(t.id)}
            className={clsx('px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors',
              tab===t.id?'border-primary-500 text-primary-600':'border-transparent text-gray-500 hover:text-gray-700')}>
            {t.label}
          </button>
        ))}
      </div>

      {/* Alerts tab */}
      {tab==='alerts' && (
        <div className={clsx('grid gap-4', selectedAlert?'grid-cols-1 lg:grid-cols-2':'grid-cols-1')}>
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
            <div className="px-5 py-4 border-b border-gray-100"><h3 className="text-sm font-semibold text-gray-700">Open Degradation Alerts ({alerts.length})</h3></div>
            {loading?<div className="flex justify-center py-12"><Loader2 size={20} className="animate-spin text-gray-400"/></div>
            :alerts.length===0?<div className="text-center py-12 text-gray-400 text-sm">No open alerts. All assets performing normally.</div>
            :<div className="divide-y divide-gray-100">
                {alerts.map(a=>(
                  <div key={a.id} onClick={()=>setSelectedAlert(a)}
                    className={clsx('px-5 py-4 hover:bg-hover cursor-pointer transition-colors', selectedAlert?.id===a.id&&'bg-hover')}>
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap mb-1">
                          <span className={clsx('px-2 py-0.5 rounded-full text-xs font-medium capitalize',ALERT_SEVERITY[a.severity])}>{a.severity}</span>
                          <span className={clsx('px-2 py-0.5 rounded-full text-xs font-medium',ALERT_STATUS[a.status]?.cls)}>{ALERT_STATUS[a.status]?.label}</span>
                        </div>
                        <p className="font-medium text-gray-900 text-sm">{a.title}</p>
                        <p className="text-xs text-gray-500 mt-0.5">{a.asset_name}</p>
                      </div>
                      <p className="text-xs text-gray-400 shrink-0">{new Date(a.created_at).toLocaleDateString('en-GB')}</p>
                    </div>
                  </div>
                ))}
              </div>}
          </div>

          {selectedAlert && (
            <div className="bg-white rounded-xl border border-gray-200 p-5 space-y-4">
              <div className="flex items-start justify-between">
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <span className={clsx('px-2 py-0.5 rounded-full text-xs font-medium capitalize',ALERT_SEVERITY[selectedAlert.severity])}>{selectedAlert.severity}</span>
                    <span className={clsx('px-2 py-0.5 rounded-full text-xs font-medium',ALERT_STATUS[selectedAlert.status]?.cls)}>{ALERT_STATUS[selectedAlert.status]?.label}</span>
                  </div>
                  <h3 className="font-semibold text-gray-900">{selectedAlert.title}</h3>
                  <p className="text-xs text-gray-400">{selectedAlert.asset_name}</p>
                </div>
                <button onClick={()=>setSelectedAlert(null)} className="text-gray-400 hover:text-gray-600 text-xl">&times;</button>
              </div>
              {selectedAlert.description && <p className="text-sm text-gray-600">{selectedAlert.description}</p>}
              <div className="grid grid-cols-2 gap-3 text-sm">
                {selectedAlert.metric_value&&<div className="bg-gray-50 rounded-lg p-3"><p className="text-xs text-gray-400">Metric Value</p><p className="font-bold text-gray-900">{selectedAlert.metric_value}</p></div>}
                {selectedAlert.threshold_value&&<div className="bg-gray-50 rounded-lg p-3"><p className="text-xs text-gray-400">Threshold</p><p className="font-bold text-gray-900">{selectedAlert.threshold_value}</p></div>}
              </div>

              {!selectedAlert.ai_analysis ? (
                <button onClick={()=>runRCA(selectedAlert.id)} disabled={rcaLoading}
                  className="w-full flex items-center justify-center gap-1.5 py-2 text-sm font-medium bg-primary-500 text-white rounded-lg hover:bg-primary-600 disabled:opacity-60">
                  {rcaLoading?<Loader2 size={14} className="animate-spin"/>:<Sparkles size={14}/>}
                  {rcaLoading?'Analysing root cause…':'Run AI Root Cause Analysis'}
                </button>
              ) : (
                <div className="bg-primary-50 border border-primary-100 rounded-xl p-4">
                  <p className="text-xs font-semibold text-primary-700 mb-1 flex items-center gap-1"><Sparkles size={11}/>AI Root Cause Analysis</p>
                  <p className="text-xs text-gray-700 leading-relaxed whitespace-pre-wrap">{selectedAlert.ai_analysis}</p>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Work Orders */}
      {tab==='workorders' && (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
            <h3 className="text-sm font-semibold text-gray-700">Work Orders ({workOrders.length})</h3>
          </div>
          {workOrders.length===0?<div className="text-center py-12 text-gray-400 text-sm">No work orders.</div>
          :<table className="w-full text-sm"><thead className="bg-gray-50 text-gray-500 text-xs uppercase">
            <tr>{['WO#','Asset','Type','Priority','Scheduled','Technician','Status',''].map(h=><th key={h} className="px-4 py-3 text-left font-medium">{h}</th>)}</tr>
          </thead>
          <tbody className="divide-y divide-gray-100">{workOrders.map((w,i)=>(
            <tr key={w.id} className={i%2===0?'bg-white':'bg-surface'}>
              <td className="px-4 py-3 font-mono text-xs text-gray-500">{w.work_order_number}</td>
              <td className="px-4 py-3 font-medium text-gray-900">{w.asset_name}</td>
              <td className="px-4 py-3 capitalize text-gray-600">{w.maintenance_type?.replace('_',' ')}</td>
              <td className="px-4 py-3"><span className={clsx('px-2 py-0.5 rounded-full text-xs font-medium capitalize',PRIORITY[w.priority])}>{w.priority}</span></td>
              <td className="px-4 py-3 text-gray-600">{w.scheduled_date?new Date(w.scheduled_date).toLocaleDateString('en-GB'):'—'}</td>
              <td className="px-4 py-3 text-gray-600">{w.technician_name||'Unassigned'}</td>
              <td className="px-4 py-3"><span className={clsx('px-2 py-0.5 rounded-full text-xs font-medium',WO_STATUS[w.status]?.cls)}>{WO_STATUS[w.status]?.label}</span></td>
              <td className="px-4 py-3">{canWrite&&w.status!=='completed'&&<button onClick={()=>updateWOStatus(w.id,'completed')} className="px-2 py-1 text-xs bg-emerald-50 text-emerald-700 rounded hover:bg-emerald-100">Complete</button>}</td>
            </tr>
          ))}</tbody></table>}
        </div>
      )}

      {/* Performance Logs */}
      {tab==='performance' && (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="px-5 py-4 border-b border-gray-100"><h3 className="text-sm font-semibold text-gray-700">Performance Logs</h3></div>
          {perfLogs.length===0?<div className="text-center py-12 text-gray-400 text-sm">No performance logs.</div>
          :<table className="w-full text-sm"><thead className="bg-gray-50 text-gray-500 text-xs uppercase">
            <tr>{['Date','Asset','Output MWh','Expected MWh','Perf Ratio','Availability %','Temp °C'].map(h=><th key={h} className="px-4 py-3 text-left font-medium">{h}</th>)}</tr>
          </thead>
          <tbody className="divide-y divide-gray-100">{perfLogs.slice(0,50).map((p,i)=>{
            const ratio = parseFloat(p.performance_ratio||0);
            return (<tr key={p.id} className={i%2===0?'bg-white':'bg-surface'}>
              <td className="px-4 py-3 text-gray-600">{p.log_date?.split('T')[0]}</td>
              <td className="px-4 py-3 font-medium text-gray-900">{p.asset_id}</td>
              <td className="px-4 py-3 font-medium text-primary-700">{Number(p.output_mwh||0).toFixed(2)}</td>
              <td className="px-4 py-3 text-gray-600">{p.expected_mwh?Number(p.expected_mwh).toFixed(2):'—'}</td>
              <td className="px-4 py-3"><span className={clsx('font-medium',ratio<0.8?'text-danger':ratio<0.9?'text-warn':'text-accent')}>{ratio?(ratio*100).toFixed(1)+'%':'—'}</span></td>
              <td className="px-4 py-3 text-gray-600">{p.availability_pct?`${p.availability_pct}%`:'—'}</td>
              <td className="px-4 py-3 text-gray-600">{p.ambient_temp_c??'—'}</td>
            </tr>);
          })}</tbody></table>}
        </div>
      )}

      {/* Work Order Modal */}
      {woModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] flex flex-col">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 shrink-0">
              <h2 className="text-base font-semibold text-gray-900">New Work Order</h2>
              <button onClick={()=>setWoModal(false)} className="text-gray-400 hover:text-gray-600 text-xl">&times;</button>
            </div>
            <div className="px-6 py-5 space-y-4 overflow-y-auto">
              <F label="Asset"><AssetSelect value={woForm.asset_id} onChange={setW('asset_id')}/></F>
              <F label="Title *"><I value={woForm.title} onChange={setW('title')}/></F>
              <div className="grid grid-cols-2 gap-4">
                <F label="Type"><S value={woForm.maintenance_type} onChange={setW('maintenance_type')}>
                  <option value="preventive">Preventive</option><option value="corrective">Corrective</option>
                  <option value="emergency">Emergency</option><option value="inspection">Inspection</option>
                </S></F>
                <F label="Priority"><S value={woForm.priority} onChange={setW('priority')}>
                  <option value="low">Low</option><option value="medium">Medium</option>
                  <option value="high">High</option><option value="critical">Critical</option>
                </S></F>
                <F label="Scheduled Date"><I type="date" value={woForm.scheduled_date} onChange={setW('scheduled_date')}/></F>
                <F label="Estimated Cost (€)"><I type="number" value={woForm.estimated_cost} onChange={setW('estimated_cost')}/></F>
              </div>
              <F label="Description"><textarea value={woForm.description} onChange={e=>setWoForm(f=>({...f,description:e.target.value}))} rows={3} className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-primary-500/30"/></F>
            </div>
            <div className="flex justify-end gap-3 px-6 py-4 border-t border-gray-100 shrink-0">
              <button onClick={()=>setWoModal(false)} className="px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-lg">Cancel</button>
              <button onClick={saveWO} disabled={saving} className="flex items-center gap-2 px-5 py-2 bg-primary-500 text-white text-sm font-medium rounded-lg hover:bg-primary-600 disabled:opacity-60">
                {saving&&<Loader2 size={14} className="animate-spin"/>} Create Work Order
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Performance Log Modal */}
      {perfModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] flex flex-col">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 shrink-0">
              <h2 className="text-base font-semibold text-gray-900">Log Performance Data</h2>
              <button onClick={()=>setPerfModal(false)} className="text-gray-400 hover:text-gray-600 text-xl">&times;</button>
            </div>
            <div className="px-6 py-5 space-y-4 overflow-y-auto">
              <div className="grid grid-cols-2 gap-4">
                <F label="Asset"><AssetSelect value={perfForm.asset_id} onChange={setP('asset_id')}/></F>
                <F label="Date"><I type="date" value={perfForm.log_date} onChange={setP('log_date')}/></F>
                <F label="Output (MWh)"><I type="number" step="0.0001" value={perfForm.output_mwh} onChange={setP('output_mwh')}/></F>
                <F label="Expected (MWh)"><I type="number" step="0.0001" value={perfForm.expected_mwh} onChange={setP('expected_mwh')}/></F>
                <F label="Availability %"><I type="number" step="0.01" value={perfForm.availability_pct} onChange={setP('availability_pct')}/></F>
                <F label="Ambient Temp °C"><I type="number" step="0.1" value={perfForm.ambient_temp_c} onChange={setP('ambient_temp_c')}/></F>
                <F label="Wind Speed m/s"><I type="number" step="0.1" value={perfForm.wind_speed_ms} onChange={setP('wind_speed_ms')}/></F>
              </div>
            </div>
            <div className="flex justify-end gap-3 px-6 py-4 border-t border-gray-100 shrink-0">
              <button onClick={()=>setPerfModal(false)} className="px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-lg">Cancel</button>
              <button onClick={savePerf} disabled={saving} className="flex items-center gap-2 px-5 py-2 bg-primary-500 text-white text-sm font-medium rounded-lg hover:bg-primary-600 disabled:opacity-60">
                {saving&&<Loader2 size={14} className="animate-spin"/>} Save Log
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
